import { create } from "zustand";
import type {
  Product,
  ProductOption,
  ReservationData,
  TicketPersonFormField,
  PickListOptions,
} from "../types/reservation";
// Type-only in the other direction, so this is not a runtime cycle: the quantity
// rules have one definition and the clamp must not drift from the spinners.
import {
  getMaxAllowedQty,
  getMinQty,
  getTicketCeiling,
} from "../pageComponents/reservationPage/utils/productQuantities";

export type {
  TicketPersonFormField,
  PickListOptions,
  ProductOption,
  StoreProduct,
  TicketOptionQuantities,
};

// ticketId -> { optionId -> quantity added }
type TicketOptionQuantities = Record<number, Record<number, number>>;

interface StoreProduct extends Product {
  optionQuantities: Record<number, number>; // optionId -> quantity added
  // Only populated when isPerTicket is true; per-order products keep using
  // optionQuantities. Exactly one of the two maps is ever non-empty.
  ticketOptionQuantities: TicketOptionQuantities;
}

// ticketId -> { dynamicAttributeId -> value }
interface TicketPersonData {
  [ticketId: number]: {
    [dynamicAttributeId: string]: string | null;
  };
}

interface ReservationState {
  reservationId: string;
  products: StoreProduct[];
  currency: string;
  ticketPersonData: TicketPersonData;
  // Ticket ids in the order getReservation delivered them. The single authority
  // for ticket numbering and for allocating shared stock across tickets.
  orderedTicketIds: number[];
  // Lifted out of the Products component so one 18+ confirmation covers every
  // carousel on the page. Deliberately survives setReservationStore/bulkRestore.
  isAgeVerified: boolean;
  // Bumped to ask every mounted Products carousel (per-order and each ticket's)
  // to close its age check, drawer and description modal — e.g. before the
  // leave or expiration modal opens. Only changes matter, not the value.
  productOverlaysCloseRequest: number;

  // Actions
  setOptionQuantity: (
    productId: number,
    optionId: number,
    quantity: number,
  ) => void;
  setTicketOptionQuantity: (
    productId: number,
    ticketId: number,
    optionId: number,
    quantity: number,
  ) => void;
  setAgeVerified: (verified: boolean) => void;
  requestCloseProductOverlays: () => void;
  setTicketPersonField: (
    ticketId: number,
    attributeId: string,
    value: string | null,
  ) => void;
  setReservationStore: (data: ReservationData) => void;
  reconcileProducts: (freshProducts: Product[]) => void;
  resetReservationStore: () => void;
  bulkRestore: (
    ticketPersonData: TicketPersonData,
    optionQuantities: Record<number, Record<number, number>>,
    ticketOptionQuantities?: Record<number, TicketOptionQuantities>,
  ) => void;
}

const initialState = {
  reservationId: "",
  products: [] as StoreProduct[],
  currency: "",
  ticketPersonData: {} as TicketPersonData,
  orderedTicketIds: [] as number[],
  isAgeVerified: false,
  productOverlaysCloseRequest: 0,
};

/**
 * Clamps a per-ticket option map to `maxQty` units in total and `ticketCeiling`
 * units per ticket, allocating forward through `orderedTicketIds`. Equivalent to
 * shedding excess from the last ticket backwards, but terminates in a single
 * pass regardless of the input values.
 *
 * The two limits are genuinely different rules and must both be applied: the
 * total is the shared stock pool, the ceiling is each ticket's own maximumOrder.
 * Ticket ids absent from `orderedTicketIds` are dropped — a stale session blob or
 * a server-released ticket would otherwise steal headroom from the real tickets
 * and ship an invalid ticketId to finalize.
 */
function allocateAcrossTickets(
  prev: TicketOptionQuantities,
  optionId: number,
  maxQty: number,
  orderedTicketIds: number[],
  ticketCeiling: number,
  minQty: number,
): Array<[number, number]> {
  const allocated: Array<[number, number]> = [];
  let remaining = maxQty;
  for (const ticketId of orderedTicketIds) {
    if (remaining <= 0) break;
    const take = Math.min(
      prev[ticketId]?.[optionId] ?? 0,
      remaining,
      ticketCeiling,
    );
    // A ticket holds none of an option or at least its minimum — never a
    // partial amount. Shedding stock down to 3+1 against a minimum of 3 would
    // strand that second ticket: its spinner floor is the minimum and its
    // ceiling is what is left, so neither button moves and the row can only be
    // deleted. The units it cannot legally hold go to the next ticket instead.
    if (take < minQty) continue;
    allocated.push([ticketId, take]);
    remaining -= take;
  }
  return allocated;
}

/**
 * Re-clamps a product's carts against its current options. Used by both
 * reconcileProducts (fresh server data) and bulkRestore (session blob), which
 * face the same hazards: vanished options, reduced stock, orphan ticket ids and
 * a product whose isPerTicket has flipped since the quantities were written.
 */
function clampProductCarts(
  product: Product,
  optionQuantities: Record<number, number>,
  ticketOptionQuantities: TicketOptionQuantities,
  orderedTicketIds: number[],
): StoreProduct {
  const isPerTicket = !!product.isPerTicket;
  const nextOptionQuantities: Record<number, number> = {};
  const nextTicketOptionQuantities: TicketOptionQuantities = {};

  for (const option of product.productOptions) {
    // For a per-ticket product maximumOrder caps each ticket, not the order, so
    // the order-wide budget is the stock pool alone — clamping the total to
    // maximumOrder here would silently shed units the customer is entitled to.
    const maxQty = isPerTicket ? option.quantity : getMaxAllowedQty(option);
    if (maxQty === 0) continue; // no stock — remove from cart

    if (isPerTicket) {
      const allocated = allocateAcrossTickets(
        ticketOptionQuantities,
        option.id,
        maxQty,
        orderedTicketIds,
        getTicketCeiling(option),
        getMinQty(option),
      );
      for (const [ticketId, qty] of allocated) {
        nextTicketOptionQuantities[ticketId] = {
          ...nextTicketOptionQuantities[ticketId],
          [option.id]: qty,
        };
      }
    } else {
      const qty = optionQuantities[option.id] ?? 0;
      if (qty > 0) nextOptionQuantities[option.id] = Math.min(qty, maxQty);
    }
  }

  // Options missing from productOptions are dropped by construction, as is the
  // map contradicting isPerTicket — quantities left in an unread map would stay
  // invisible in the UI and the payload while still being priced and shipped.
  return {
    ...product,
    isPerTicket,
    optionQuantities: nextOptionQuantities,
    ticketOptionQuantities: nextTicketOptionQuantities,
  };
}

export const useReservationStore = create<ReservationState>((set) => ({
  ...initialState,

  // Writes a literal 0 rather than deleting the key — removeProductOption in
  // order-info.tsx relies on it. Contrast setTicketOptionQuantity below.
  setOptionQuantity: (productId, optionId, quantity) =>
    set((state) => ({
      products: state.products.map((product) =>
        product.id === productId
          ? {
              ...product,
              optionQuantities: {
                ...product.optionQuantities,
                [optionId]: quantity,
              },
            }
          : product,
      ),
    })),

  // Deletes on 0 rather than writing it, and drops the ticket bucket once empty,
  // so consumers iterating ticketOptionQuantities never see empty buckets or
  // zero-quantity lines. Deliberately asymmetric with setOptionQuantity above.
  setTicketOptionQuantity: (productId, ticketId, optionId, quantity) =>
    set((state) => ({
      products: state.products.map((product) => {
        if (product.id !== productId) return product;

        const nextTicket = { ...product.ticketOptionQuantities[ticketId] };
        if (quantity > 0) {
          nextTicket[optionId] = quantity;
        } else {
          delete nextTicket[optionId];
        }

        const next = { ...product.ticketOptionQuantities };
        if (Object.keys(nextTicket).length > 0) {
          next[ticketId] = nextTicket;
        } else {
          delete next[ticketId];
        }

        return { ...product, ticketOptionQuantities: next };
      }),
    })),

  setAgeVerified: (verified) => set({ isAgeVerified: verified }),

  requestCloseProductOverlays: () =>
    set((state) => ({
      productOverlaysCloseRequest: state.productOverlaysCloseRequest + 1,
    })),

  setTicketPersonField: (ticketId, attributeId, value) =>
    set((state) => ({
      ticketPersonData: {
        ...state.ticketPersonData,
        [ticketId]: {
          ...state.ticketPersonData[ticketId],
          [attributeId]: value,
        },
      },
    })),

  setReservationStore: (data) =>
    set({
      reservationId: data.reservationId,
      products: (data.products ?? []).map((p) => ({
        ...p,
        isPerTicket: !!p.isPerTicket,
        optionQuantities: {},
        ticketOptionQuantities: {},
      })),
      currency: data.currency,
      ticketPersonData: {},
      orderedTicketIds: (data.tickets ?? []).map((t) => t.id),
    }),

  // reconcileProducts when there is a products error on finalize
  reconcileProducts: (freshProducts) =>
    set((state) => {
      const prevMap = new Map(state.products.map((p) => [p.id, p]));

      const reconciled = freshProducts.map((fresh) => {
        const prev = prevMap.get(fresh.id);

        // New product not previously in store — add it with empty cart
        if (!prev) {
          return {
            ...fresh,
            isPerTicket: !!fresh.isPerTicket,
            optionQuantities: {},
            ticketOptionQuantities: {},
          };
        }

        return clampProductCarts(
          fresh,
          prev.optionQuantities,
          prev.ticketOptionQuantities,
          state.orderedTicketIds,
        );
      });

      return { products: reconciled };
    }),

  resetReservationStore: () => set(initialState),

  bulkRestore: (
    ticketPersonData,
    optionQuantities,
    ticketOptionQuantities = {},
  ) =>
    set((state) => ({
      ticketPersonData,
      // Restored quantities go through the same clamp as reconcileProducts: a
      // blob written before stock changed can otherwise exceed the current cap
      // and leave Add disabled everywhere with no explanation.
      products: state.products.map((p) =>
        clampProductCarts(
          p,
          optionQuantities[p.id] ?? p.optionQuantities,
          ticketOptionQuantities[p.id] ?? p.ticketOptionQuantities,
          state.orderedTicketIds,
        ),
      ),
    })),
}));
